import { useEffect, useRef, useState, Children, cloneElement, isValidElement } from "react";

/**
 * <Reveal variant="fade-up" delay={0} stagger={80} once>{children}</Reveal>
 *
 * variants:
 *  - fade-up     (default)
 *  - fade-down
 *  - fade-left
 *  - fade-right
 *  - scale
 *  - blur
 *  - mask        (clip-path reveal, good for headings)
 *
 * Use `stagger` when the direct children should each animate with a cascading delay.
 */
export const Reveal = ({
  children,
  variant = "fade-up",
  delay = 0,
  stagger = 0,
  duration = 900,
  once = true,
  threshold = 0.15,
  className = "",
  as: Tag = "div",
  ...rest
}) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShow(true);
            if (once) obs.unobserve(entry.target);
          } else if (!once) {
            setShow(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    // Failsafe: if the observer never fires (page-transition timing, edge
    // cases in some browsers/automation), don't leave real content stuck
    // permanently invisible — force it visible after a short grace period.
    const failsafe = setTimeout(() => setShow(true), 2500);
    return () => {
      obs.disconnect();
      clearTimeout(failsafe);
    };
  }, [once, threshold]);

  const baseStyle = {
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionProperty: "opacity, transform, filter, clip-path",
    transitionDelay: `${delay}ms`,
    willChange: "opacity, transform",
  };

  const stateClass = `reveal-${variant} ${show ? "is-visible" : ""}`;

  // When stagger, clone children and add incremental delays
  if (stagger > 0) {
    return (
      <Tag ref={ref} className={className} {...rest}>
        {Children.map(children, (child, i) => {
          if (!isValidElement(child)) return child;
          const childDelay = delay + i * stagger;
          return cloneElement(child, {
            className: `${child.props.className || ""} ${stateClass}`.trim(),
            style: {
              ...(child.props.style || {}),
              ...baseStyle,
              transitionDelay: `${childDelay}ms`,
            },
          });
        })}
      </Tag>
    );
  }

  return (
    <Tag ref={ref} className={`${stateClass} ${className}`.trim()} style={baseStyle} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
